import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva, VariantProps } from 'class-variance-authority';
import { motion } from 'framer-motion';
import { MouseEvent, ReactNode, useEffect } from 'react';
import useToasts from './useToasts';

const toastClassNames = cva(
    'tw-pointer-events-auto tw-relative tw-w-full sm:tw-w-[360px] tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-py-3 tw-pl-4 tw-pr-2.5 tw-shadow-lg',
    {
        variants: {
            color: {
                success: '',
                warning: '',
                error: '',
                alert: '',
                default: '',
                info: ''
            },
            isPressable: {
                true: 'tw-cursor-pointer'
            }
        },
        defaultVariants: {
            color: 'default',
            isPressable: false
        }
    }
);

const accentClassNames = cva('tw-absolute tw-inset-y-0 tw-left-0 tw-w-[3px]', {
    variants: {
        color: {
            success: 'tw-bg-success',
            warning: 'tw-bg-warning',
            error: 'tw-bg-error',
            alert: 'tw-bg-warning',
            default: 'tw-bg-accents-4',
            info: 'tw-bg-accents-4'
        }
    },
    defaultVariants: {
        color: 'default'
    }
});

const iconBadgeClassNames = cva(
    'tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-xs',
    {
        variants: {
            color: {
                success: 'tw-bg-successLight tw-text-success',
                warning: 'tw-bg-warningLight tw-text-warning',
                error: 'tw-bg-errorLight tw-text-error',
                alert: 'tw-bg-warningLight tw-text-warning',
                default: 'tw-bg-accents-1 tw-text-accents-8',
                info: 'tw-bg-accents-1 tw-text-accents-8'
            }
        },
        defaultVariants: {
            color: 'default'
        }
    }
);

const progressClassNames = cva('tw-absolute tw-bottom-0 tw-left-0 tw-h-[2px]', {
    variants: {
        color: {
            success: 'tw-bg-success/40',
            warning: 'tw-bg-warning/40',
            error: 'tw-bg-error/40',
            alert: 'tw-bg-warning/40',
            default: 'tw-bg-accents-4',
            info: 'tw-bg-accents-4'
        }
    },
    defaultVariants: {
        color: 'default'
    }
});

interface Props extends VariantProps<typeof toastClassNames> {
    onPress?: () => any | undefined;
    title: string;
    description: string;
    content?: ReactNode;
    rightContent?: ReactNode;
    icon?: IconProp;
    toastId: string;
    removeAfter?: number;
}

// Compact toast card: colored accent stripe + icon badge, auto-dismiss with a progress bar
const Toast = ({
    onPress,
    title,
    content,
    rightContent,
    description,
    color,
    icon,
    isPressable,
    toastId,
    removeAfter
}: Props) => {
    const removeToast = useToasts((state) => state.removeToast);

    useEffect(() => {
        if (!removeAfter) return;
        const timeout = setTimeout(() => removeToast(toastId), removeAfter);
        return () => clearTimeout(timeout);
    }, [removeToast, toastId, removeAfter]);

    // Close via the X button without triggering the card's own click handler
    const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        removeToast(toastId);
    };

    return (
        <motion.div
            layout="position"
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
                opacity: 0,
                scale: 0.97,
                zIndex: -1,
                transition: {
                    duration: 0.15
                }
            }}
            transition={{
                layout: { duration: 0.2 },
                default: {
                    type: 'spring',
                    mass: 1,
                    damping: 30,
                    stiffness: 300
                }
            }}
            onClick={() => {
                if (!!onPress) {
                    onPress?.();
                    return;
                }

                if (removeAfter) {
                    removeToast(toastId);
                }
            }}
            className={toastClassNames({ isPressable: !!onPress || isPressable || !!removeAfter, color })}
        >
            <span className={accentClassNames({ color })} />
            <div className="tw-flex tw-items-center tw-gap-3">
                {!!icon && (
                    <span className={iconBadgeClassNames({ color })}>
                        <FontAwesomeIcon icon={icon} />
                    </span>
                )}
                <div className="tw-min-w-0 tw-flex-1">
                    {title && (
                        <h5 className="tw-mb-0 tw-text-sm tw-font-semibold tw-leading-tight tw-text-accents-9">
                            {title}
                        </h5>
                    )}
                    {description && (
                        <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-leading-snug tw-text-accents-8">{description}</p>
                    )}
                </div>
                {!!rightContent && (
                    <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-center">{rightContent}</div>
                )}
                <button
                    type="button"
                    onClick={handleClose}
                    className="tw-flex tw-h-6 tw-w-6 tw-shrink-0 !tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-p-0 tw-text-accents-6 hover:tw-bg-accents-1 hover:tw-text-accents-9"
                >
                    <FontAwesomeIcon className="tw-text-xs" icon={faTimes} />
                </button>
            </div>
            {content && <div className={icon ? 'tw-mt-2 tw-pl-10' : 'tw-mt-2'}>{content}</div>}
            {!!removeAfter && (
                <motion.div
                    className={progressClassNames({ color })}
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: removeAfter / 1000, ease: 'linear' }}
                />
            )}
        </motion.div>
    );
};

export default Toast;
